/** @jsxImportSource @emotion/react */
import * as React from "react";
import { OrganizationUISettings } from "../../model/OrganizationUISettings";
import * as _ from "lodash";
import { FormProvider, useForm } from "react-hook-form";
import { CheckboxInput } from "../../components/forms/inputs/CheckboxInput";
import { TextInput } from "../../components/forms/inputs/TextInput";
import { ColorPickerInput } from "../../components/forms/inputs/ColorPickerInput";
import { css } from "@emotion/react";
import { SelectInput } from "../../components/forms/inputs/SelectInput";

const graphTypeOptions = [
  { label: "Barchart", value: "barchart" },
  { label: "Linechart", value: "linechart" },
  { label: "Piechart", value: "piechart" },
  { label: "Metric", value: "metric" },
];

const sectionGap = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const checkboxGap = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

type Props = {
  onSubmit: (data: OrganizationUISettings) => void;
  uiSettings: OrganizationUISettings;
  isUpsertingUISettings: boolean;
};

export const OrganizationSettingsUpsertForm = ({
  onSubmit,
  uiSettings,
  isUpsertingUISettings,
}: Props) => {
  const hasUISettings = (): boolean => {
    return !!uiSettings && !_.isEmpty(uiSettings);
  };

  const uiSettingsToFormData = (values: OrganizationUISettings) => {
    if (!hasUISettings()) {
      values = {
        ...values,
        userListColumns: [
          "name",
          "email",
          "status",
          "joinDate",
          "signupCountry",
        ],
        userListActions: ["accept", "reject", "view"],
        userListFilters: ["nameOrEmail", "country", "status", "phoneNb"],
        venueListColumns: ["image", "title", "tags", "active", "address"],
        venueListActions: ["edit"],
        venueListFilters: ["name", "isActive", "country", "tag"],
        userProfileDetails: [
          "facebook",
          "userId",
          "email",
          "phoneNb",
          "employeeGroup",
          "gender",
        ],
        venueToggableEditFields: [
          "eventDate",
          "showMemberCard",
          "showPersonalCode",
        ],
        statsGraphList: [],
        isUserProfileMixpanelLinkVisible: true,
        isUserProfileStripeLinkVisible: true,
        isUserProfileBalanceVisible: false,
      };
    }

    const reduceFn = (acc: any, el: string) => ({ ...acc, [el]: true });

    return {
      userListColumns: _.reduce(values.userListColumns, reduceFn, {}),
      userListActions: _.reduce(values.userListActions, reduceFn, {}),
      userListFilters: _.reduce(values.userListFilters, reduceFn, {}),
      venueListColumns: _.reduce(values.venueListColumns, reduceFn, {}),
      venueListActions: _.reduce(values.venueListActions, reduceFn, {}),
      venueListFilters: _.reduce(values.venueListFilters, reduceFn, {}),
      venueToggableEditFields: _.reduce(
        values.venueToggableEditFields,
        reduceFn,
        {}
      ),
      userProfileDetails: _.reduce(values.userProfileDetails, reduceFn, {}),
      mixpanelLink: values.mixpanelLink,
      facebookAnalyticsLink: values.facebookAnalyticsLink,
      dashboardBannerSrc: values.dashboardBannerSrc,
      dashboardBannerSrcSet: values.dashboardBannerSrcSet,
      logoSrc: values.logoSrc,
      statsGraphList: values.statsGraphList,
      isUserProfileMixpanelLinkVisible: values.isUserProfileMixpanelLinkVisible,
      isUserProfileStripeLinkVisible: values.isUserProfileStripeLinkVisible,
      isUserProfileBalanceVisible: values.isUserProfileBalanceVisible,
      colorPrimary: values.colorPrimary,
      colorPrimaryContrast: values.colorPrimaryContrast,
      colorPrimaryHighlight: values.colorPrimaryHighlight,
      isVoucherVisibleOnPromotionCode: true,
    };
  };

  const methods = useForm<OrganizationUISettings>({
    defaultValues: {
      ...uiSettingsToFormData(uiSettings),
    },
  });
  const handleSubmit = (values: OrganizationUISettings) => {
    if (values.statsGraphList && values.statsGraphList.length) {
      values.statsGraphList = values.statsGraphList.filter(
        (x) => x && x.url && x.name
      );
    }

    onSubmit(values);
  };

  const formDataToUISettings = (values: any): OrganizationUISettings => {
    const reduceFn = (acc: any, value: boolean, key: string) => {
      if (!value) {
        return acc;
      }

      return [...acc, key];
    };

    return {
      userListColumns: _.reduce(values.userListColumns, reduceFn, []),
      userListActions: _.reduce(values.userListActions, reduceFn, []),
      userListFilters: _.reduce(values.userListFilters, reduceFn, []),
      venueListColumns: _.reduce(values.venueListColumns, reduceFn, []),
      venueListActions: _.reduce(values.venueListActions, reduceFn, []),
      venueListFilters: _.reduce(values.venueListFilters, reduceFn, []),
      venueToggableEditFields: _.reduce(
        values.venueToggableEditFields,
        reduceFn,
        []
      ),
      userProfileDetails: _.reduce(values.userProfileDetails, reduceFn, []),
      mixpanelLink: values.mixpanelLink,
      facebookAnalyticsLink: values.facebookAnalyticsLink,
      dashboardBannerSrc: values.dashboardBannerSrc,
      dashboardBannerSrcSet: values.dashboardBannerSrcSet,
      logoSrc: values.logoSrc,
      statsGraphList: values.statsGraphList,
      isUserProfileMixpanelLinkVisible: values.isUserProfileMixpanelLinkVisible,
      isUserProfileStripeLinkVisible: values.isUserProfileStripeLinkVisible,
      isUserProfileBalanceVisible: values.isUserProfileBalanceVisible,
      colorPrimary: values.colorPrimary,
      colorPrimaryContrast: values.colorPrimaryContrast,
      colorPrimaryHighlight: values.colorPrimaryHighlight,
      isVoucherVisibleOnPromotionCode: true,
    };
  };

  if (!uiSettings) {
    return null;
  }

  return (
    /* @ts-ignore */
    <FormProvider {...methods}>
      <form className="card">
        <div className="card-header">
          <h3 className="card-title">Organization UI Settings Edit</h3>
        </div>
        {!hasUISettings() && (
          <div className="card-alert alert alert-success mb-0">
            Currently there is no config set for this organization.
          </div>
        )}
        <div css={sectionGap} className="card-body">
          <div className="row">
            <div css={checkboxGap} className="col-md-6">
              <label className="form-label">User list columns</label>
              <CheckboxInput label="Name" name="userListColumns.name" />
              <CheckboxInput label="Email" name="userListColumns.email" />
              <CheckboxInput label="Status" name="userListColumns.status" />
              <CheckboxInput
                label="Join Date"
                name="userListColumns.joinDate"
              />
              <CheckboxInput
                label="Signup Country"
                name="userListColumns.signupCountry"
              />
            </div>

            <div css={checkboxGap} className="col-md-6">
              <label className="form-label">User list filters</label>
              <CheckboxInput
                label="Name or email"
                name="userListFilters.nameOrEmail"
              />
              <CheckboxInput label="Country" name="userListFilters.country" />
              <CheckboxInput label="Status" name="userListFilters.status" />
              <CheckboxInput
                label="Phone number"
                name="userListFilters.phoneNb"
              />
            </div>
          </div>

          <div className="row">
            <div css={checkboxGap} className="col-md-6">
              <label className="form-label">User list actions</label>
              <CheckboxInput label="Accept" name="userListActions.accept" />
              <CheckboxInput label="Reject" name="userListActions.reject" />
              <CheckboxInput label="View" name="userListActions.view" />
            </div>
          </div>

          <div className="row">
            <div css={checkboxGap} className="col-md-4">
              <label className="form-label">Venue list columns</label>
              <CheckboxInput label="Image" name="venueListColumns.image" />
              <CheckboxInput label="Title" name="venueListColumns.title" />
              <CheckboxInput label="Tags" name="venueListColumns.tags" />
              <CheckboxInput label="Active" name="venueListColumns.active" />
              <CheckboxInput label="Address" name="venueListColumns.address" />
            </div>

            <div css={checkboxGap} className="col-md-4">
              <label className="form-label">Venue list actions</label>
              <CheckboxInput label="Edit" name="venueListActions.edit" />
              <label className="form-label">Venue list filters</label>
              <CheckboxInput label="Name" name="venueListFilters.name" />
              <CheckboxInput
                label="Is Active"
                name="venueListFilters.isActive"
              />
              <CheckboxInput label="Country" name="venueListFilters.country" />
              <CheckboxInput label="Tag" name="venueListFilters.tag" />
            </div>

            <div css={checkboxGap} className="col-md-4">
              <label className="form-label">Venue edit</label>
              <CheckboxInput
                label="Show Personal Code"
                name="venueToggableEditFields.showPersonalCode"
              />
              <CheckboxInput
                label="Show member card"
                name="venueToggableEditFields.showMemberCard"
              />
              <CheckboxInput
                label="Event date"
                name="venueToggableEditFields.eventDate"
              />
            </div>
          </div>

          <div className="row">
            <div css={checkboxGap} className="col-md-4">
              <label className="form-label">User Profile details</label>
              <CheckboxInput
                name="userProfileDetails.facebook"
                label="Facebook"
              />
              <CheckboxInput name="userProfileDetails.userId" label="User Id" />
              <CheckboxInput name="userProfileDetails.email" label="Email" />
              <CheckboxInput
                name="userProfileDetails.phoneNb"
                label="Phone number"
              />
              <CheckboxInput name="userProfileDetails.gender" label="Gender" />
              <CheckboxInput
                label="Mixpanel link"
                name="isUserProfileMixpanelLinkVisible"
              />
              <CheckboxInput
                label="Stripe link"
                name="isUserProfileStripeLinkVisible"
              />
              <CheckboxInput
                label="Balance"
                name="isUserProfileBalanceVisible"
              />
            </div>
          </div>
          <div className="row">
            <div css={sectionGap} className="col-md-6">
              <TextInput name="mixpanelLink" label="Mixpanel link" />
              <TextInput
                name="facebookAnalyticsLink"
                label="Facebook Analytics Link"
              />
              <TextInput name="logoSrc" label="Logo src" />
            </div>
            <div css={sectionGap} className="col-md-6">
              <TextInput
                name="dashboardBannerSrc"
                label="Dashbard banner src"
              />
              <TextInput
                name="dashboardBannerSrcSet"
                label="Dashbard banner srcSet"
              />
            </div>
          </div>
          <div className="row">
            <div css={sectionGap} className="col-md-6">
              <ColorPickerInput label="Primary color" name="colorPrimary" />
              <ColorPickerInput
                label="Primary color contrast"
                name="colorPrimaryContrast"
              />
            </div>
            <div className="col-md-6">
              <ColorPickerInput
                label="Primary color highlight"
                name="colorPrimaryHighlight"
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="row">
                {_.range(6).map((i) => (
                  <React.Fragment key={i}>
                    <div className="col-md-4 pr-1 mb-2">
                      <TextInput
                        name={`statsGraphList.${i}.name`}
                        label={`Graph Name ${i + 1}`}
                      />
                    </div>
                    <div className="col-md-2 pl-0">
                      <TextInput
                        name={`statsGraphList.${i}.url`}
                        label={`Url ${i + 1}`}
                      />
                    </div>
                    <div className="col-md-2 pl-0">
                      <TextInput
                        name={`statsGraphList.${i}.graphDesc`}
                        label={`Graph Description ${i + 1}`}
                      />
                    </div>
                    <div className="col-md-2 pl-0">
                      <TextInput
                        name={`statsGraphList.${i}.dataDesc`}
                        label={`Data Description ${i + 1}`}
                      />
                    </div>
                    <div className="col-md-2 pl-0">
                      <label className="form-label">Graph Type</label>
                      <SelectInput
                        name={`statsGraphList.${i}.graphType`}
                        id="graphType"
                        options={graphTypeOptions}
                      />
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="card-footer text-right">
          <button
            className="btn btn-primary"
            type="button"
            disabled={isUpsertingUISettings}
            onClick={methods.handleSubmit((values) =>
              handleSubmit(formDataToUISettings(values))
            )}
          >
            Update
          </button>
        </div>
      </form>
    </FormProvider>
  );
};
